interface FinanceSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name (visible label text or an i18n string). */
  label?: string;
  /** Id of a visible element that names the switch (preferred over `label`). */
  labelledBy?: string;
  describedBy?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
}

/**
 * Accessible on/off switch (role="switch", DESIGN_SYSTEM.md §6). The knob sits
 * on the start side when off and moves toward the end when on, in both RTL and
 * LTR. Brand colour marks the "on" state only.
 */
export default function FinanceSwitch({ checked, onChange, label, labelledBy, describedBy, disabled, size = 'md', id }: FinanceSwitchProps) {
  const track = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
  const knob = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const move = size === 'sm' ? 'ltr:translate-x-4 rtl:-translate-x-4' : 'ltr:translate-x-5 rtl:-translate-x-5';
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex flex-shrink-0 items-center rounded-full p-0.5 transition-colors duration-150 disabled:opacity-50 ${track} ${
        checked ? 'bg-accent-hover' : 'bg-surface-300'
      }`}
    >
      <span
        aria-hidden="true"
        className={`inline-block rounded-full bg-on-accent shadow-xs transition-transform duration-150 ${knob} ${checked ? move : 'translate-x-0'}`}
      />
    </button>
  );
}
